// src/data/friendshipAssessmentData.js - Friendship Vibe Check Assessment

export const friendshipAssessmentConfig = {
  title: 'Friendship Vibe Check 🌟',
  description: 'Discover your unique friendship style and get real insights about your social circle dynamics.',
  totalQuestions: 23,
  timeEstimate: '10-15 minutes',
  instructions: 'Choose what feels most true for you - there are no right or wrong answers! This is about understanding YOUR unique way of doing friendship. 💜',
  categories: [
    'Friendship Style',
    'Friend Group Dynamics',
    'Values & Boundaries',
    'Social Energy',
    'Growth & Evolution',
    'Conflict Navigation',
  ],
};

export const friendshipAssessmentQuestions = [
  // Friendship Style (5 questions)
  {
    id: 1,
    category: 'Friendship Style',
    text: "When a mate is going through a rough patch, your natural instinct is to:",
    options: [
      "Drop everything and show up at their door with snacks and tissues",
      "Send thoughtful messages checking in, but give them space to process",
      "Research solutions and send them helpful resources or advice",
      "Rally the friend group to create a support network around them",
    ],
    archetypeWeights: {
      anchor: [4, 2, 1, 3],
      catalyst: [3, 1, 2, 4],
      sage: [2, 3, 4, 1],
      connector: [3, 2, 1, 4],
      guardian: [4, 3, 2, 1],
      mirror: [3, 4, 1, 2],
    },
    dimensionScores: {
      emotionalAvailability: [4, 3, 2, 3],
      practicalSupport: [3, 2, 4, 3],
      groupDynamics: [2, 1, 1, 4],
    },
  },
  
  {
    id: 2,
    category: 'Friendship Style',
    text: "Your friends would most likely come to you when they need:",
    options: [
      "Someone to just listen without trying to fix everything",
      "Honest feedback, even if it might be hard to hear",
      "A distraction and some laughs to forget their troubles",
      "Help making a difficult decision or solving a problem",
    ],
    archetypeWeights: {
      anchor: [3, 2, 1, 2],
      catalyst: [1, 2, 4, 1],
      sage: [2, 3, 1, 4],
      connector: [2, 1, 3, 2],
      guardian: [3, 4, 1, 3],
      mirror: [4, 2, 2, 1],
    },
    dimensionScores: {
      emotionalAvailability: [4, 2, 2, 3],
      conflictResolution: [2, 4, 1, 3],
      socialCourage: [2, 4, 3, 2],
    },
  },
  
  {
    id: 3,
    category: 'Friendship Style',
    text: "In your friend group, you naturally tend to be:",
    options: [
      "The one who remembers everyone's important dates and checks in regularly",
      "The planner who organises gatherings and keeps everyone connected",
      "The mood-maker who brings energy and spontaneity to the group",
      "The neutral one who mediates when there's tension",
    ],
    archetypeWeights: {
      anchor: [4, 2, 1, 3],
      catalyst: [1, 3, 4, 1],
      sage: [2, 1, 1, 4],
      connector: [2, 4, 3, 2],
      guardian: [4, 3, 1, 2],
      mirror: [3, 2, 2, 3],
    },
    dimensionScores: {
      friendshipMaintenance: [4, 4, 2, 3],
      groupDynamics: [3, 4, 3, 2],
      emotionalAvailability: [4, 2, 2, 3],
    },
  },
  
  {
    id: 4,
    category: 'Friendship Style',
    text: "When meeting someone new who could become a friend, you usually:",
    options: [
      "Share something personal early on to establish genuine connection",
      "Keep things light and fun initially, depth comes later if at all",
      "Ask lots of questions to understand who they really are",
      "Take ages to warm up - trust needs to be earned slowly",
    ],
    archetypeWeights: {
      anchor: [2, 1, 3, 4],
      catalyst: [3, 4, 2, 1],
      sage: [2, 1, 4, 3],
      connector: [4, 3, 2, 1],
      guardian: [1, 2, 3, 4],
      mirror: [3, 2, 4, 2],
    },
    dimensionScores: {
      socialCourage: [4, 3, 3, 1],
      boundaryConfidence: [2, 4, 3, 4],
      emotionalAvailability: [4, 2, 3, 1],
    },
  },
  
  {
    id: 5,
    category: 'Friendship Style',
    text: "Your approach to maintaining friendships looks like:",
    options: [
      "Consistent, regular check-ins even when life gets busy",
      "Intense periods of closeness, then might go quiet for ages",
      "Always available in crisis, but struggle with everyday maintenance",
      "Quality over quantity - deep investment in just a few close friends",
    ],
    archetypeWeights: {
      anchor: [4, 1, 3, 3],
      catalyst: [2, 4, 2, 1],
      sage: [3, 2, 2, 4],
      connector: [4, 3, 2, 2],
      guardian: [3, 1, 4, 3],
      mirror: [3, 3, 2, 4],
    },
    dimensionScores: {
      friendshipMaintenance: [4, 2, 3, 3],
      reciprocityAwareness: [3, 2, 2, 4],
      socialEnergy: [3, 2, 4, 4],
    },
  },
  
  // Friend Group Dynamics (4 questions)
  {
    id: 6,
    category: 'Friend Group Dynamics',
    text: "If your friend group was a TV show, it would probably be:",
    options: [
      "A wholesome sitcom where everyone genuinely supports each other through everything",
      "A reality show - entertaining with occasional drama but real connections underneath",
      "An anthology series - different friends for different parts of your life who rarely overlap",
      "A long-running drama - deep history, complex relationships, some unresolved tensions",
    ],
    archetypeWeights: {
      anchor: [4, 2, 2, 3],
      catalyst: [3, 4, 2, 2],
      sage: [3, 1, 3, 2],
      connector: [4, 3, 1, 2],
      guardian: [4, 2, 2, 3],
      mirror: [3, 2, 2, 4],
    },
    dimensionScores: {
      groupHealth: [4, 2, 3, 2],
      diversityIndex: [2, 3, 4, 2],
      conflictResolution: [4, 2, 3, 1],
    },
  },
  
  {
    id: 7,
    category: 'Friend Group Dynamics',
    text: "Looking at your main friend group honestly, you'd say:",
    options: [
      "We're all pretty similar - shared values, interests, and life stages",
      "Wildly diverse - sometimes wonder how we all ended up together",
      "A few distinct subgroups that you bridge between",
      "It's changed a lot lately - some growing apart, new people coming in",
    ],
    archetypeWeights: {
      anchor: [3, 2, 2, 2],
      catalyst: [2, 4, 3, 3],
      sage: [3, 2, 2, 2],
      connector: [2, 3, 4, 2],
      guardian: [4, 1, 2, 2],
      mirror: [3, 2, 3, 3],
    },
    dimensionScores: {
      diversityIndex: [1, 4, 3, 3],
      groupHealth: [3, 3, 2, 2],
      growthPotential: [2, 4, 3, 4],
    },
  },
  
  {
    id: 8,
    category: 'Friend Group Dynamics',
    text: "When there's conflict in your friend group, you typically:",
    options: [
      "Stay neutral and try to see all perspectives without taking sides",
      "Actively mediate and work to bring people back together",
      "Feel incredibly uncomfortable and withdraw until it blows over",
      "Have strong opinions about who's right and aren't afraid to say so",
    ],
    archetypeWeights: {
      anchor: [3, 2, 1, 2],
      catalyst: [1, 3, 1, 4],
      sage: [4, 3, 2, 2],
      connector: [2, 4, 1, 2],
      guardian: [2, 3, 1, 4],
      mirror: [4, 3, 3, 1],
    },
    dimensionScores: {
      conflictResolution: [3, 4, 1, 2],
      socialCourage: [2, 3, 1, 4],
      groupDynamics: [3, 4, 1, 2],
    },
  },
  
  {
    id: 9,
    category: 'Friend Group Dynamics',
    text: "The balance in your friendships feels like:",
    options: [
      "You're usually the one giving more support than you receive",
      "Pretty balanced - everyone shows up for each other equally",
      "You're lucky to have friends who give so much to you",
      "It varies wildly depending on which friend and what period of life",
    ],
    archetypeWeights: {
      anchor: [3, 4, 2, 3],
      catalyst: [2, 3, 2, 3],
      sage: [3, 3, 2, 3],
      connector: [2, 4, 2, 3],
      guardian: [4, 3, 1, 3],
      mirror: [3, 4, 3, 3],
    },
    dimensionScores: {
      reciprocityAwareness: [2, 4, 3, 3],
      boundaryConfidence: [2, 4, 3, 3],
      emotionalAvailability: [4, 3, 2, 3],
    },
  },
  
  // Values & Boundaries (4 questions)
  {
    id: 10,
    category: 'Values & Boundaries',
    text: "When a friend repeatedly cancels plans or doesn't follow through, you:",
    options: [
      "Address it directly but kindly - clear communication is essential",
      "Start naturally investing less in that friendship without saying anything",
      "Keep giving them chances - everyone goes through tough times",
      "Feel hurt but pretend it's fine to avoid confrontation",
    ],
    archetypeWeights: {
      anchor: [3, 2, 3, 1],
      catalyst: [3, 3, 2, 1],
      sage: [4, 3, 2, 1],
      connector: [3, 2, 3, 2],
      guardian: [4, 2, 3, 1],
      mirror: [2, 2, 4, 3],
    },
    dimensionScores: {
      boundaryConfidence: [4, 3, 1, 1],
      conflictResolution: [4, 2, 2, 1],
      reciprocityAwareness: [3, 4, 1, 2],
    },
  },
  
  {
    id: 11,
    category: 'Values & Boundaries',
    text: "Your biggest friendship dealbreaker would be:",
    options: [
      "Betraying your trust or sharing your private information",
      "Lack of reciprocity - always taking but never giving",
      "Being judgemental about your life choices or other friends",
      "Dishonesty - even small lies erode the foundation",
    ],
    archetypeWeights: {
      anchor: [3, 3, 2, 4],
      catalyst: [3, 3, 3, 3],
      sage: [3, 2, 4, 3],
      connector: [3, 3, 3, 3],
      guardian: [4, 2, 3, 4],
      mirror: [4, 3, 2, 3],
    },
    dimensionScores: {
      boundaryConfidence: [4, 3, 3, 4],
      reciprocityAwareness: [3, 4, 2, 3],
      values: [4, 3, 3, 4],
    },
  },
  
  {
    id: 12,
    category: 'Values & Boundaries',
    text: "When friends ask for favours or help, your typical response is:",
    options: [
      "Immediately say yes, then sometimes regret overcommitting",
      "Consider carefully if you have capacity before responding",
      "Usually say yes for close friends, no for acquaintances",
      "Often suggest alternatives if you can't help directly",
    ],
    archetypeWeights: {
      anchor: [3, 4, 3, 3],
      catalyst: [2, 3, 2, 3],
      sage: [1, 4, 3, 4],
      connector: [3, 3, 2, 4],
      guardian: [4, 3, 4, 2],
      mirror: [4, 3, 3, 2],
    },
    dimensionScores: {
      boundaryConfidence: [1, 4, 3, 3],
      reciprocityAwareness: [2, 4, 3, 3],
      practicalSupport: [4, 3, 3, 3],
    },
  },
  
  {
    id: 13,
    category: 'Values & Boundaries',
    text: "Your view on sharing friends' secrets or personal information is:",
    options: [
      "Vault-level security - you'd never share anything told in confidence",
      "Depends on the situation - sometimes people need help you can't provide",
      "If it's not explicitly a secret, it might come up naturally in conversation",
      "Close friends know each other's business - some sharing is expected",
    ],
    archetypeWeights: {
      anchor: [4, 3, 1, 1],
      catalyst: [3, 2, 3, 2],
      sage: [4, 3, 1, 1],
      connector: [3, 2, 3, 3],
      guardian: [4, 4, 1, 1],
      mirror: [4, 3, 2, 2],
    },
    dimensionScores: {
      boundaryConfidence: [4, 3, 2, 1],
      trustworthiness: [4, 3, 2, 1],
      values: [4, 3, 2, 2],
    },
  },
  
  // Social Energy (4 questions)
  {
    id: 14,
    category: 'Social Energy',
    text: "After a big social weekend with multiple friend gatherings, you typically feel:",
    options: [
      "Energised and already planning the next hangout",
      "Satisfied but definitely need some alone time to recharge",
      "Exhausted and questioning why you said yes to everything",
      "It completely depends on which friends and what we did",
    ],
    archetypeWeights: {
      anchor: [2, 3, 1, 3],
      catalyst: [4, 2, 1, 3],
      sage: [1, 3, 2, 3],
      connector: [4, 2, 1, 3],
      guardian: [2, 3, 2, 3],
      mirror: [2, 3, 2, 3],
    },
    dimensionScores: {
      socialEnergy: [4, 2, 1, 3],
      selfAwareness: [3, 4, 3, 3],
      boundaryConfidence: [3, 4, 2, 3],
    },
  },
  
  {
    id: 15,
    category: 'Social Energy',
    text: "Your ideal friendship maintenance schedule would be:",
    options: [
      "Daily texts or voice notes with your core group",
      "Weekly catch-ups with different friends on rotation",
      "Monthly deep-dive hangouts with meaningful conversation",
      "Flexible - connecting when it feels natural, no forced schedules",
    ],
    archetypeWeights: {
      anchor: [4, 4, 2, 2],
      catalyst: [3, 3, 2, 4],
      sage: [2, 3, 4, 3],
      connector: [4, 4, 2, 3],
      guardian: [4, 3, 3, 2],
      mirror: [3, 3, 3, 4],
    },
    dimensionScores: {
      friendshipMaintenance: [4, 4, 3, 2],
      socialEnergy: [4, 3, 2, 3],
      structurePreference: [4, 4, 3, 1],
    },
  },
  
  {
    id: 16,
    category: 'Social Energy',
    text: "When you're going through something difficult, you tend to:",
    options: [
      "Reach out to multiple friends for different types of support",
      "Confide in one or two trusted people only",
      "Process alone first, then share once you've figured things out",
      "Accidentally isolate and struggle to ask for help",
    ],
    archetypeWeights: {
      anchor: [2, 3, 3, 1],
      catalyst: [3, 2, 2, 1],
      sage: [2, 3, 4, 2],
      connector: [4, 2, 2, 1],
      guardian: [1, 3, 4, 3],
      mirror: [3, 3, 3, 2],
    },
    dimensionScores: {
      emotionalAvailability: [3, 3, 2, 1],
      socialCourage: [4, 3, 2, 1],
      supportSeeking: [4, 3, 2, 1],
    },
  },
  
  {
    id: 17,
    category: 'Social Energy',
    text: "The number of close friendships you can genuinely maintain is:",
    options: [
      "2-3 maximum - you go deep with very few people",
      "4-6 - a solid inner circle with room for everyone",
      "7-10 - you have energy for a larger close network",
      "It fluctuates - sometimes many, sometimes you withdraw",
    ],
    archetypeWeights: {
      anchor: [3, 4, 2, 2],
      catalyst: [1, 3, 4, 3],
      sage: [4, 3, 1, 2],
      connector: [1, 3, 4, 3],
      guardian: [3, 4, 2, 2],
      mirror: [4, 3, 2, 3],
    },
    dimensionScores: {
      socialEnergy: [2, 3, 4, 2],
      depthPreference: [4, 3, 2, 3],
      friendshipMaintenance: [3, 4, 3, 2],
    },
  },
  
  // Growth & Evolution (3 questions)
  {
    id: 18,
    category: 'Growth & Evolution',
    text: "When you and a friend start growing in different directions, you usually:",
    options: [
      "Let the friendship naturally fade without drama or discussion",
      "Have an honest conversation about how the friendship is changing",
      "Try harder to maintain the connection despite the differences",
      "Feel guilty but accept that not all friendships are meant to last",
    ],
    archetypeWeights: {
      anchor: [2, 3, 4, 3],
      catalyst: [3, 3, 2, 3],
      sage: [3, 4, 2, 3],
      connector: [2, 3, 3, 3],
      guardian: [2, 3, 4, 2],
      mirror: [3, 4, 3, 3],
    },
    dimensionScores: {
      growthMindset: [2, 4, 2, 3],
      conflictResolution: [2, 4, 2, 3],
      acceptanceOfChange: [3, 3, 2, 4],
    },
  },
  
  {
    id: 19,
    category: 'Growth & Evolution',
    text: "Your openness to making new friends at this point in your life is:",
    options: [
      "Very open - always room for new connections and perspectives",
      "Selective - quality over quantity, but open to the right people",
      "Pretty closed - you have your people and that's enough",
      "Depends on the context - easier in some settings than others",
    ],
    archetypeWeights: {
      anchor: [2, 3, 2, 3],
      catalyst: [4, 3, 1, 3],
      sage: [2, 4, 3, 3],
      connector: [4, 3, 1, 3],
      guardian: [2, 3, 4, 2],
      mirror: [3, 3, 2, 3],
    },
    dimensionScores: {
      growthPotential: [4, 3, 1, 3],
      socialCourage: [4, 3, 1, 3],
      openness: [4, 3, 1, 3],
    },
  },
  
  {
    id: 20,
    category: 'Growth & Evolution',
    text: "Looking back at ended friendships, you mostly feel:",
    options: [
      "Grateful for what they brought to your life during that time",
      "Some regret about how things ended or what was left unsaid",
      "Relief - they ended for good reasons even if it was hard",
      "A mix - some were necessary endings, others still hurt",
    ],
    archetypeWeights: {
      anchor: [3, 2, 3, 3],
      catalyst: [3, 2, 3, 3],
      sage: [4, 2, 3, 3],
      connector: [3, 3, 2, 3],
      guardian: [3, 3, 3, 3],
      mirror: [4, 3, 2, 3],
    },
    dimensionScores: {
      emotionalMaturity: [4, 2, 3, 3],
      acceptanceOfChange: [4, 2, 3, 3],
      growthMindset: [4, 2, 3, 3],
    },
  },
  
  // Conflict Navigation (3 questions)
  {
    id: 21,
    category: 'Conflict Navigation',
    text: "When you've hurt a friend's feelings unintentionally, you typically:",
    options: [
      "Apologise immediately and try to make it right as soon as possible",
      "Give them space first, then reach out when emotions have settled",
      "Feel terrible and overthink it but struggle to address it directly",
      "Explain your intentions to help them understand you didn't mean harm",
    ],
    archetypeWeights: {
      anchor: [4, 3, 1, 2],
      catalyst: [3, 2, 1, 3],
      sage: [3, 4, 1, 3],
      connector: [4, 3, 2, 2],
      guardian: [4, 3, 2, 2],
      mirror: [4, 3, 3, 2],
    },
    dimensionScores: {
      conflictResolution: [3, 3, 1, 2],
      emotionalAvailability: [4, 3, 2, 2],
      socialCourage: [3, 3, 1, 2],
    },
  },
  
  {
    id: 22,
    category: 'Conflict Navigation',
    text: "When two of your friends are in conflict with each other, you:",
    options: [
      "Stay completely neutral and refuse to get involved",
      "Listen to both sides but encourage them to talk directly",
      "Actively try to mediate and fix the situation",
      "Find it super stressful and distance yourself until it's resolved",
    ],
    archetypeWeights: {
      anchor: [3, 4, 2, 1],
      catalyst: [2, 3, 3, 1],
      sage: [4, 4, 2, 2],
      connector: [2, 3, 4, 1],
      guardian: [3, 4, 3, 1],
      mirror: [3, 4, 2, 2],
    },
    dimensionScores: {
      conflictResolution: [2, 4, 3, 1],
      groupDynamics: [3, 4, 4, 1],
      boundaryConfidence: [4, 3, 2, 1],
    },
  },
  
  {
    id: 23,
    category: 'Conflict Navigation',
    text: "Your general attitude toward conflict in friendships is:",
    options: [
      "It's healthy and necessary for growth when handled maturely",
      "Uncomfortable but inevitable - you deal with it when you must",
      "To be avoided if possible - harmony is more important",
      "Depends entirely on what the conflict is about",
    ],
    archetypeWeights: {
      anchor: [3, 3, 2, 3],
      catalyst: [3, 2, 1, 3],
      sage: [4, 3, 1, 3],
      connector: [3, 2, 3, 3],
      guardian: [4, 3, 2, 3],
      mirror: [3, 3, 3, 3],
    },
    dimensionScores: {
      conflictResolution: [4, 3, 1, 3],
      emotionalMaturity: [4, 3, 2, 3],
      growthMindset: [4, 3, 2, 3],
    },
  },
  {
    id: 24,
    category: 'Values & Boundaries',
    text: "When a close friend makes new friends or spends lots of time with others, you:",
    options: [
      "Feel genuinely happy they're expanding their social circle",
      "Feel a bit left out but know it's healthy for them to have other friends",
      "Get anxious they're replacing you and need reassurance",
      "Feel hurt and betrayed - close friends should prioritise each other",
    ],
    archetypeWeights: {
      anchor: [4, 3, 1, 0],
      catalyst: [4, 3, 2, 1],
      sage: [4, 3, 1, 0],
      connector: [4, 3, 1, 0],
      guardian: [3, 3, 2, 1],
      mirror: [3, 3, 2, 1],
    },
    dimensionScores: {
      boundaryConfidence: [4, 3, 1, 0],
      emotionalMaturity: [4, 3, 1, 0],
      possessiveness: [0, 1, 3, 4], // New dimension for problematic patterns
    },
  },
  
  {
    id: 25,
    category: 'Social Energy',
    text: "How often do you find yourself in the middle of friend drama or conflicts?",
    options: [
      "Rarely - I actively avoid drama and keep things peaceful",
      "Sometimes - normal amount for any friend group",
      "Often - somehow I always end up involved even when I try not to be",
      "Constantly - there's always something going on in my friend circle",
    ],
    archetypeWeights: {
      anchor: [4, 3, 1, 0],
      catalyst: [3, 3, 2, 1],
      sage: [4, 3, 1, 0],
      connector: [3, 3, 2, 1],
      guardian: [3, 3, 2, 1],
      mirror: [3, 3, 2, 1],
    },
    dimensionScores: {
      groupHealth: [4, 3, 1, 0],
      dramaAttraction: [0, 1, 3, 4], // New dimension
      conflictResolution: [4, 3, 1, 0],
    },
  },
  
  {
    id: 26,
    category: 'Friendship Style',
    text: "When you need emotional support from friends, you typically:",
    options: [
      "Reach out to different friends for different types of support",
      "Have one or two go-to people who you turn to",
      "Constantly need reassurance and validation from multiple friends",
      "Struggle to ask for help even when really struggling",
    ],
    archetypeWeights: {
      anchor: [3, 4, 0, 1],
      catalyst: [3, 3, 1, 1],
      sage: [3, 4, 0, 2],
      connector: [4, 3, 1, 1],
      guardian: [2, 3, 0, 3],
      mirror: [3, 4, 1, 2],
    },
    dimensionScores: {
      emotionalAvailability: [3, 4, 1, 1],
      supportSeeking: [3, 3, 4, 0],
      emotional_regulation: [3, 4, 0, 2], // New dimension
    },
  },
];

// Friendship Archetypes with detailed descriptions
export const friendshipArchetypes = {
  anchor: {
    name: 'The Anchor',
    emoji: '⚓',
    shortDesc: 'The reliable, steady presence everyone can count on',
    color: 'from-blue-600 to-blue-500',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-400',
    traits: [
      'Consistently reliable and trustworthy',
      'Excellent at maintaining long-term friendships',
      'Provides stability in group dynamics',
      'Sometimes struggles to ask for help themselves',
    ],
    growth: 'Learning to receive support as readily as you give it',
    superpower: 'Creating safe, stable spaces where friendships can flourish',
  },
  catalyst: {
    name: 'The Catalyst',
    emoji: '⚡',
    shortDesc: 'The one who sparks adventures and brings energy',
    color: 'from-yellow-600 to-orange-500',
    bgColor: 'bg-yellow-500/10',
    textColor: 'text-yellow-400',
    traits: [
      'Brings energy and spontaneity to every gathering',
      'Great at initiating new experiences and connections',
      'Can struggle with consistency in maintenance',
      'Natural ability to lift group mood and energy',
    ],
    growth: 'Building consistency alongside your natural spontaneity',
    superpower: 'Transforming ordinary moments into memorable adventures',
  },
  sage: {
    name: 'The Sage',
    emoji: '🦉',
    shortDesc: 'The wise listener who gives thoughtful advice',
    color: 'from-purple-600 to-purple-500',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400',
    traits: [
      'Exceptional listening and advice-giving skills',
      'Processes deeply before responding',
      'Offers perspective others often miss',
      'May overthink social interactions',
    ],
    growth: 'Balancing wisdom-sharing with lighthearted connection',
    superpower: 'Helping friends see their situations with clarity and wisdom',
  },
  connector: {
    name: 'The Connector',
    emoji: '🌐',
    shortDesc: 'The social glue who brings people together',
    color: 'from-green-600 to-teal-500',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-400',
    traits: [
      'Natural networker who introduces friends to each other',
      'Maintains multiple friend groups successfully',
      'Excellent at group dynamics and inclusion',
      'Sometimes spreads themselves too thin',
    ],
    growth: 'Deepening select friendships while maintaining your network',
    superpower: 'Creating community and connection wherever you go',
  },
  guardian: {
    name: 'The Guardian',
    emoji: '🛡️',
    shortDesc: 'The protective friend who looks out for everyone',
    color: 'from-red-600 to-pink-500',
    bgColor: 'bg-red-500/10',
    textColor: 'text-red-400',
    traits: [
      'Fiercely protective of friends\' wellbeing',
      'First to notice when someone is struggling',
      'Strong boundaries around loyalty and trust',
      'Can be overly protective or involved',
    ],
    growth: 'Allowing friends to make their own mistakes and grow',
    superpower: 'Making everyone feel protected and valued in your presence',
  },
  mirror: {
    name: 'The Mirror',
    emoji: '🪞',
    shortDesc: 'The empathetic friend who reflects and validates',
    color: 'from-indigo-600 to-indigo-500',
    bgColor: 'bg-indigo-500/10',
    textColor: 'text-indigo-400',
    traits: [
      'Exceptional empathy and emotional intelligence',
      'Validates and reflects friends\' experiences',
      'Creates deep, meaningful connections',
      'May absorb others\' emotions too readily',
    ],
    growth: 'Maintaining your own emotional boundaries while supporting others',
    superpower: 'Making people feel truly seen and understood',
  },
};

// Results structure for different score ranges
export const friendshipAssessmentResults = [
  {
    range: [0, 30],
    level: 'Friendship Red Flags 🚨',
    shortLevel: 'Crisis Mode',
    message: "Your friendship patterns are probably causing you (and possibly others) significant pain right now. This isn't about being a bad person - it's about learned behaviours that aren't serving you. The brilliant news? These patterns can absolutely be changed with awareness and practice.",
    vibeCheck: 'Your friendships need urgent attention',
    priority: 'urgent',
    insights: [
      'You might be exhibiting clingy, controlling, or avoidant behaviours that push people away',
      'There\'s likely a mismatch between what you want from friendships and how you\'re showing up',
      'Past hurts might be driving current destructive patterns',
      'Professional support alongside these modules could accelerate your growth',
    ],
    honestTalk: 'Some of your friendship behaviours might be toxic - not because you\'re toxic, but because you haven\'t learned healthier patterns yet. This is completely fixable.',
  },
  {
    range: [31, 50],
    level: 'Friendship Works in Progress 🌱',
    shortLevel: 'Growing Phase',
    message: "You've got some solid friendship foundations, but there are definitely some patterns that need addressing. You might be struggling with consistency, boundaries, or reciprocity. The good news is you're perfectly placed to grow with some focused work.",
    vibeCheck: 'Your friendships have potential but need work',
    priority: 'moderate',
    insights: [
      'You have good instincts but might lack consistency',
      'Some behaviours could be pushing friends away without you realising',
      'You might be either too clingy or too distant at times',
      'Building confidence in healthy friendship patterns is your next step',
    ],
    honestTalk: 'You\'re not a bad friend, but some of your patterns might be making friendships harder than they need to be.',
  },
  {
    range: [51, 65],
    level: 'Solid Friend Game 💪',
    shortLevel: 'Strong Foundations',
    message: "You've developed healthy friendship patterns and maintain meaningful connections. You're doing a lot right, and now it's about optimising and fine-tuning what's already working.",
    vibeCheck: 'You\'re a quality friend with room to grow',
    priority: 'enhancement',
    insights: [
      'Your friendship skills are above average',
      'You balance giving and receiving relatively well',
      'You\'re ready for more advanced friendship dynamics',
      'Consider how you might mentor others in friendship skills',
    ],
  },
  {
    range: [66, 80],
    level: 'Friendship Excellence ✨',
    shortLevel: 'Thriving',
    message: "Your friendship game is seriously strong. You've mastered the basics and developed sophisticated skills for maintaining meaningful connections. You're the friend everyone wishes they had!",
    vibeCheck: 'You\'re absolutely nailing the friendship thing',
    priority: 'mastery',
    insights: [
      'You\'ve developed exceptional friendship skills',
      'Your social circle likely looks to you for guidance',
      'You balance multiple friendships with grace',
      'Consider how you can help others develop these skills',
    ],
  },
  {
    range: [81, 100],
    level: 'Friendship Legend Status 👑',
    shortLevel: 'Master Level',
    message: "You've basically achieved friendship enlightenment. Your ability to create, maintain, and navigate friendships is exceptional. Time to write the guidebook for the rest of us!",
    vibeCheck: 'You\'re the friendship GOAT',
    priority: 'legacy',
    insights: [
      'Your friendship skills are in the top tier',
      'You naturally create healthy, supportive communities',
      'People gravitate toward your authentic presence',
      'Your next challenge: scaling your impact to help others',
    ],
  },
];

// Dimension descriptions for tracking
export const friendshipDimensions = {
  emotionalAvailability: {
    name: 'Emotional Availability',
    description: 'How present and open you are to friends\' emotional needs',
  },
  boundaryConfidence: {
    name: 'Boundary Confidence',
    description: 'Your ability to set and maintain healthy boundaries',
  },
  socialCourage: {
    name: 'Social Courage',
    description: 'Willingness to be vulnerable and authentic in friendships',
  },
  reciprocityAwareness: {
    name: 'Reciprocity Awareness',
    description: 'Understanding of give-and-take balance in relationships',
  },
  friendshipMaintenance: {
    name: 'Friendship Maintenance',
    description: 'Consistency in nurturing and maintaining connections',
  },
  conflictResolution: {
    name: 'Conflict Resolution',
    description: 'Ability to navigate disagreements constructively',
  },
  groupHealth: {
    name: 'Group Health',
    description: 'The overall wellbeing of your friend ecosystem',
  },
  diversityIndex: {
    name: 'Diversity Index',
    description: 'Variety and range in your friendship circle',
  },
  growthPotential: {
    name: 'Growth Potential',
    description: 'Openness to evolving friendships and making new connections',
  },
  practicalSupport: {
    name: 'Practical Support',
    description: 'Your ability to provide tangible help and assistance to friends',
  },
  supportSeeking: {
    name: 'Support Seeking',
    description: 'Your comfort level with asking for help and support from others',
  },
  emotionalMaturity: {
    name: 'Emotional Maturity',
    description: 'Your ability to manage emotions and respond thoughtfully in friendships',
  },
  groupDynamics: {
    name: 'Group Dynamics',
    description: 'Your understanding and navigation of friend group interactions',
  },
  socialEnergy: {
    name: 'Social Energy',
    description: 'Your capacity for social interactions and how you manage it',
  },
  trustworthiness: {
    name: 'Trustworthiness',
    description: 'Your reliability and ability to maintain confidence',
  },
  values: {
    name: 'Shared Values',
    description: 'Alignment of core values with your friendships',
  },
  selfAwareness: {
    name: 'Self-Awareness',
    description: 'Understanding of your own social needs and impact on others',
  },
  depthPreference: {
    name: 'Depth Preference',
    description: 'Your preference for deep vs. surface-level connections',
  },
  structurePreference: {
    name: 'Structure Preference',
    description: 'Your preference for planned vs. spontaneous social interactions',
  },
  possessiveness: {
    name: 'Possessiveness',
    description: 'Tendency to be overly protective or controlling in friendships',
  },
  dramaAttraction: {
    name: 'Drama Attraction',
    description: 'Tendency to engage with or create dramatic situations',
  },
  emotional_regulation: {
    name: 'Emotional Regulation',
    description: 'Ability to manage your emotional responses in social situations',
  },
};

// Category insights for results
export const friendshipCategoryInsights = {
  'Friendship Style': {
    high: "Your friendship style is authentic and consistent. You know who you are as a friend and show up genuinely in your relationships.",
    medium: "Your friendship style varies depending on context. Finding more consistency in how you show up could strengthen your connections.",
    low: "You might still be figuring out your friendship style. Experimenting with different ways of connecting could help you find your authentic approach.",
  },
  'Friend Group Dynamics': {
    high: "Your friend group dynamics are healthy and balanced. You've created or found yourself in supportive, functional friendship ecosystems.",
    medium: "Your friend group has its strengths and challenges. Some attention to group dynamics could improve everyone's experience.",
    low: "Your friend group dynamics might be causing stress. Consider whether these dynamics serve you and what changes might help.",
  },
  'Values & Boundaries': {
    high: "You have clear values and boundaries in friendships and communicate them effectively. This creates healthy, respectful relationships.",
    medium: "Your boundaries are developing but inconsistent. Strengthening them could reduce friendship stress significantly.",
    low: "Boundary setting might be a growth area for you. Learning to advocate for your needs could transform your friendships.",
  },
  'Social Energy': {
    high: "You understand and manage your social energy excellently. You know your limits and honour them while maintaining connections.",
    medium: "Your social energy management is improving. Better understanding your social needs could help you thrive.",
    low: "Social energy might be a struggle. Learning to balance social time with recharge time could be game-changing.",
  },
  'Growth & Evolution': {
    high: "You handle friendship changes with grace and wisdom. You understand that friendships evolve and adapt accordingly.",
    medium: "You're learning to navigate friendship changes. More comfort with evolution could ease transitions.",
    low: "Friendship changes might be particularly difficult for you. Accepting the natural ebb and flow could reduce anxiety.",
  },
  'Conflict Navigation': {
    high: "You handle friendship conflicts with maturity and skill. You see conflict as an opportunity for growth rather than a threat.",
    medium: "Your conflict navigation skills are developing. More confidence in addressing issues could strengthen friendships.",
    low: "Conflict in friendships might feel overwhelming. Building conflict resolution skills could make friendships feel safer.",
  },
};

export default {
  friendshipAssessmentConfig,
  friendshipAssessmentQuestions,
  friendshipArchetypes,
  friendshipAssessmentResults,
  friendshipDimensions,
  friendshipCategoryInsights,
};